import {
    CmsPageContentProps,
    CmsPageSeoProps,
    ComponentProps,
    ComponentTabbedContentLayoutType,
    ComponentTabbedContentProps,
    ElementCtaProps,
    ElementResponsiveImageProps,
    ComponentsType,
} from "api/cms-website/cms-website.types";
import { BlogPostProps } from "api/cms-website/cms-blog-posts.types";
import { BlocksContent } from "@strapi/blocks-react-renderer";

import { getImageUrl } from "utils/imageUrl";
import { reportToSentry } from "utils/reportToSentry";
import {
    I18nLocale,
    I18nLocaleToUTSLocale,
    SentryErrorType,
    StrapiLocale,
    StrapiLocaleToI18n,
    UTSLocaleToLanguage,
} from "constants/enum";
import { cleanHtml } from "utils/cleanHtml";
import { kebabCase } from "utils/kebabCase";
import { formatDurationInMinutes } from "utils/date";
import getMinutesOfReading from "utils/getMinutesOfReading";
import { buildCmsBlogPostHreflangAlternates } from "utils/cmsHreflang";
import { extractTextFromStrapiBlockNode } from "utils/extractTextFromStrapiBlockNode";
import { mapItemsWithIcon } from "utils/cmsComponentMatcher";
import { QuestionsProps } from "components/Relaunch/Questions";

import { mapComponentTypename } from "./componentMapper";
import type { StrapiComponentPropsUnion } from "../types/strapi-components";

const isDevelopment = process.env.NODE_ENV === "development";
const strapiBase = process.env.STRAPI_WEBSITE_API_URL;

/**
 * Recursively removes undefined values from an object, replacing them with null
 * This is needed for Next.js JSON serialization which doesn't allow undefined
 */
function removeUndefined<T>(obj: T): any {
    if (obj === null || obj === undefined) {
        return null;
    }

    if (Array.isArray(obj)) {
        return obj.map(removeUndefined);
    }

    if (typeof obj === "object") {
        const cleaned: any = {};
        for (const [key, value] of Object.entries(obj)) {
            if (value === undefined) {
                // Skip undefined values entirely (don't include them in the object)
                continue;
            }
            cleaned[key] = removeUndefined(value);
        }
        return cleaned;
    }

    return obj;
}

/**
 * Transforms GraphQL response data to CmsPageContentProps format
 */
/**
 * Transforms GraphQL response data to CmsPageContentProps format
 *
 * @param graphqlData - Array of page data from GraphQL query
 * @param locale - Locale string for the page
 * @returns Transformed page data or null if no data
 */
export function transformPageData(
    graphqlData: any,
    locale: string
): CmsPageContentProps | null {
    if (!Array.isArray(graphqlData) || graphqlData.length === 0) {
        return null;
    }

    const page = graphqlData[0];
    const components = transformComponents(page.components);
    const transformedLocalizations = transformLocalizations(page.localizations);
    const transformedSeo = transformSeo(page.seo);

    const result: CmsPageContentProps = {
        id: parseInt(page.documentId || "0", 10),
        documentId: page.documentId || "",
        name: page.name || "",
        slug: page.slug || "",
        locale: (locale as I18nLocale) || I18nLocale.SPAIN_ES,
        ...(transformedSeo && { seo: transformedSeo }),
        ...(page.noFooterMarginTop != null && {
            noFooterMarginTop: page.noFooterMarginTop,
        }),
        ...(page.withHeader != null && {
            withHeader: page.withHeader,
        }),
        ...(page.withFooter != null && {
            withFooter: page.withFooter,
        }),
        ...(page.darkMode != null && {
            darkMode: page.darkMode,
        }),
        ...(page.hideMenu != null && {
            hideMenu: page.hideMenu,
        }),
        ...(transformedLocalizations && {
            localizations: transformedLocalizations,
        }),
        ...(components && {
            components: components.map((component) => ({
                selected: [component],
            })),
        }),
    };

    // Remove all undefined values for Next.js JSON serialization
    return removeUndefined(result) as CmsPageContentProps;
}

/**
 * Transforms components from GraphQL format to frontend format
 *
 * Note: components is an array of Component entities, each with a selected dynamiczone
 *
 * @param components - Array of component entities from GraphQL
 * @returns Array of transformed components or null if no components
 */
function transformComponents(
    components:
        | Array<{
              documentId: string;
              selected: Array<StrapiComponentPropsUnion | null>;
          }>
        | null
        | undefined
): ComponentProps[] | null {
    // components is an array of Component entities
    if (!Array.isArray(components) || components.length === 0) {
        return null;
    }

    // Each component has a selected dynamiczone (array with max 1 item)
    const allSelectedComponents: ComponentProps[] = [];

    for (const componentEntity of components) {
        const selected = componentEntity.selected;

        if (!selected || !Array.isArray(selected)) {
            continue;
        }

        // Transform each selected component
        for (const component of selected) {
            if (!component) {
                continue;
            }

            // First map __typename to __component (before normalizing aliases)
            const mapped = mapComponentTypename(component);
            // Then normalize aliases back to original field names
            const normalized = normalizeAliases(mapped);

            if (!normalized || !normalized.__component) {
                const errorContext = {
                    component: (component as any).__typename,
                    normalized,
                };

                if (isDevelopment) {
                    // eslint-disable-next-line no-console
                    console.warn(
                        "[transformComponents] Component missing __component:",
                        errorContext
                    );
                } else {
                    reportToSentry({
                        error: SentryErrorType.COMPONENT_MISSING_TYPE,
                        level: "warning",
                        extra: errorContext,
                    });
                }
                continue;
            }

            const transformed = transformComponent(normalized as any);
            if (transformed) {
                allSelectedComponents.push(transformed);
            } else {
                const errorContext = {
                    __component: normalized.__component,
                    normalized,
                };

                if (isDevelopment) {
                    // eslint-disable-next-line no-console
                    console.warn(
                        "[transformComponents] Component not transformed:",
                        errorContext
                    );
                } else {
                    reportToSentry({
                        error: SentryErrorType.COMPONENT_TRANSFORMATION_FAILED,
                        level: "warning",
                        extra: errorContext,
                    });
                }
            }
        }
    }

    return allSelectedComponents.length > 0 ? allSelectedComponents : null;
}

// Map of aliases to original field names
const ALIAS_MAP: Record<string, string> = {
    bannerPaidSocialTitle: "title",
    bannerPaidSocialText: "text",
    bannerPaidSocialCta: "cta",
    bannerPaidSocialAdvantages: "advantages",
    clinicsMapTitle: "title",
    mainBannerHeaderTitle: "title",
    mainBannerHeaderText: "text",
    mainBannerHeaderCta: "cta",
    mainBannerTitle: "title",
    mainBannerText: "text",
    mainBannerCta: "cta",
    mainBannerAdvantages: "advantages",
    mainBannerInvisalign: "invisalign",
    mainBannerExtraTitle: "title",
    mainBannerExtraAdvantages: "advantages",
    twoColumnListTitle: "title",
    textWithCtaText: "text",
    textWithCtaTitle: "title",
    textWithCtaCta: "cta",
    textWithCtaBaseTitle: "title",
    textWithCtaBaseText: "text",
    textWithCtaBaseCta: "cta",
    textWithCtaBaseImage: "image",
    advantagesWithHeadingTitle: "title",
    advantagesWithHeadingCta: "cta",
    advantagesWithHeadingAdvantages: "advantages",
    multiColumnSliderTitle: "title",
    multiColumnSliderCta: "cta",
    questionsWithImageTitle: "title",
    questionsWithImageCta: "cta",
    questionsTitle: "title",
    leadFormTitle: "title",
    leadFormImage: "image",
    treatmentInfoTitle: "title",
    treatmentInfoSubtitle: "subtitle",
    treatmentInfoItems: "items",
    treatmentInfoItemImage: "image",
    treatmentInfoItemBackground: "bgColor",
    multiColumnTitle: "title",
    multiColumnSubtitle: "subtitle",
    multiColumnItems: "items",
    multiColumnCta: "cta",
    illustratedListTitle: "title",
    illustratedListSubtitle: "subtitle",
    illustratedListText: "text",
    illustratedListCta: "cta",
    illustratedListItems: "items",
    VerticalAdvantagesTitle: "title",
    VerticalAdvantagesSubtitle: "subtitle",
    VerticalAdvantagesCta: "cta",
    VerticalAdvantagesImage: "image",
    VerticalAdvantagesItems: "advantages",
    HalfTextAndImageBannerTitle: "title",
    HalfTextAndImageBannerSubtitle: "subtitle",
    HalfTextAndImageBannerImage: "image",
    verticalListWithImageTitle: "title",
    verticalListWithImageSubtitle: "subtitle",
    verticalListWithImageCta: "cta",
    verticalListWithImageBgImage: "bgImage",
    verticalListWithImageItems: "items",
    verticalListSectionTitle: "title",
    verticalListSectionSubtitle: "subtitle",
    verticalListSectionCta: "cta",
    verticalListSectionItems: "items",
    treatmentIncludesSectionTitle: "title",
    treatmentIncludesSectionImage: "image",
    treatmentIncludesSectionItems: "items",
    tableWithCheckmarksTitle: "title",
    tableWithCheckmarksColumns: "columns",
    tableWithCheckmarksCta: "cta",
    tableWithCheckmarksRows: "rows",
    sliderTitle: "title",
    sliderCta: "cta",
    sliderItems: "items",
    sliderTrustpilotLink: "trustpilotLink",
    imageCardGridTitle: "title",
    imageCardGridItems: "items",
    infoBannerTitle: "title",
    infoBannerText: "text",
    infoBannerItems: "items",
    infoBannerCta: "cta",
    listWithCtaTitle: "title",
    listWithCtaText: "text",
    listWithCtaItems: "items",
    listWithCtaCta: "cta",
    followUsTitle: "title",
    followUsSubtitle: "subtitle",
    followUsSocials: "socials",
    videoTitle: "title",
    videoText: "text",
    videoCaption: "caption",
    referralBannerTitle: "title",
    referralBannerSubtitle: "subtitle",
    referralBannerDescription: "description",
    referralBannerButtonText: "buttonText",
    referralBannerToastText: "toastText",
    referralBannerCards: "cards",
    referralHeroTitle: "title",
    referralHeroSubtitle: "subtitle",
    referralHeroDescription: "description",
    referralHeroCard: "card",
    termLinkText: "text",
};

/**
 * Normalizes GraphQL aliases back to original field names
 * This allows transformers to work with consistent field names
 *
 * @param component - Component with GraphQL aliases (e.g., mainBannerTitle)
 * @returns Component with normalized field names (e.g., title)
 */
function normalizeAliases(component: any): any {
    if (!component || typeof component !== "object") {
        return component;
    }

    const normalized: any = { ...component };

    // Replace aliased fields with original names
    for (const [alias, original] of Object.entries(ALIAS_MAP)) {
        if (normalized[alias] !== undefined) {
            normalized[original] = normalized[alias];
            delete normalized[alias];
        }
    }

    return normalized;
}

/**
 * Type-safe transformation of advantages array based on component type
 * Uses runtime type guards to ensure correct structure after normalizeAliases
 */
function transformAdvantages(
    component: any,
    componentType: ComponentsType
): any[] | undefined {
    if (!component.advantages || !Array.isArray(component.advantages)) {
        return undefined;
    }

    switch (componentType) {
        case ComponentsType.BANNER_PAID_SOCIAL:
        case ComponentsType.MAIN_BANNER:
        case ComponentsType.ADVANTAGES_WITH_HEADING: {
            // After normalizeAliases, advantages should have title/subtitle structure
            // Type guard ensures runtime safety
            return component.advantages
                .filter(
                    (a: unknown): a is { title: string; subtitle: string } =>
                        a !== null &&
                        a !== undefined &&
                        typeof a === "object" &&
                        "title" in a &&
                        "subtitle" in a &&
                        typeof (a as any).title === "string" &&
                        typeof (a as any).subtitle === "string"
                )
                .map((a: { title: string; subtitle: string }) => ({
                    title: a.title,
                    subtitle: a.subtitle,
                }));
        }

        case ComponentsType.MAIN_BANNER_EXTRA: {
            // After normalizeAliases, advantages should have text structure
            const filtered = component.advantages.filter(
                (a: unknown): a is { text: string } | string =>
                    a !== null &&
                    a !== undefined &&
                    (typeof a === "string" ||
                        (typeof a === "object" &&
                            "text" in a &&
                            typeof (a as any).text === "string"))
            );
            return filtered.map((a: { text: string } | string) => ({
                text: typeof a === "string" ? a : a.text,
            }));
        }

        default: {
            // Fallback for unknown component types
            const fallback = component.advantages.map((advantage: any) => {
                if (typeof advantage === "string" || advantage?.text) {
                    return { text: advantage.text || advantage };
                }
                return advantage;
            });

            if (!isDevelopment) {
                reportToSentry({
                    error: SentryErrorType.COMPONENT_TRANSFORMATION_FAILED,
                    level: "warning",
                    extra: {
                        component: componentType,
                        field: "advantages",
                        structure: component.advantages[0],
                    },
                });
            }

            return fallback;
        }
    }
}

const clampStarRating = (value: unknown): 1 | 2 | 3 | 4 | 5 | undefined => {
    if (typeof value !== "number" || Number.isNaN(value)) {
        return undefined;
    }
    const rounded = Math.round(value);
    const clamped = Math.min(5, Math.max(1, rounded));
    return clamped as 1 | 2 | 3 | 4 | 5;
};

function transformTabbedContentComponent(
    component: any
): ComponentProps | null {
    const id = parseInt(component.id || "0", 10);
    const layoutType = component.type as ComponentTabbedContentLayoutType;

    const header = (component.header || [])
        .filter(Boolean)
        .map((h: { title?: string; description?: string }) => ({
            title: h.title || "",
            description: h.description || "",
        }));

    const tabs = (component.tabs || [])
        .filter(Boolean)
        .map(
            (tab: {
                title?: string;
                subtitle?: string | null;
                content?: unknown[] | null;
            }) => ({
                title: tab.title || "",
                ...(tab.subtitle && { subtitle: tab.subtitle }),
                content: (tab.content || [])
                    .filter(Boolean)
                    .map((item: unknown) => {
                        const row: Record<string, unknown> = {};
                        const entry = item as Record<string, unknown>;
                        const itemRating = clampStarRating(entry.rating);

                        if (layoutType === "TWO_THREE_COLUMN") {
                            row.title = String(entry.title ?? "");
                            row.text = String(entry.text ?? "");
                            row.customerName = String(entry.customerName ?? "");
                            row.date = String(entry.date ?? "");
                            row.rating = itemRating ?? 5;
                        } else {
                            if (entry.image) {
                                row.image = transformResponsiveImage(
                                    entry.image
                                );
                            }
                            if (entry.label) {
                                row.label = entry.label;
                            }
                            if (entry.title != null) {
                                row.title = entry.title || "";
                            }
                            if (entry.text != null) {
                                row.text = entry.text || "";
                            }
                            if (entry.customerName) {
                                row.customerName = entry.customerName;
                            }
                            if (entry.date) {
                                row.date = entry.date;
                            }
                            if (itemRating !== undefined) {
                                row.rating = itemRating;
                            }
                        }

                        return row;
                    }),
            })
        );

    /** One block heading per tab index; CMS may send fewer headers than tabs. */
    const headerAlignedToTabs =
        tabs.length === 0
            ? header
            : tabs.map(
                  (
                      tab: {
                          title: string;
                          subtitle?: string;
                          content: unknown[];
                      },
                      index: number
                  ) => ({
                      title: header[index]?.title || tab.title || "",
                      description: header[index]?.description ?? "",
                  })
              );

    const rootRating = clampStarRating(component.rating);

    const result: ComponentTabbedContentProps = {
        id,
        __component: ComponentsType.TABBED_CONTENT,
        noMarginBottom: component.noMarginBottom,
        type: layoutType,
        content: {
            header: headerAlignedToTabs,
            tabs,
            showMoreButtonLabel: component.showMoreButtonLabel || "",
            ...(component.disclaimer && { disclaimer: component.disclaimer }),
            ...(rootRating !== undefined && { rating: rootRating }),
        },
    };

    return removeUndefined(result) as ComponentProps;
}

/**
 * Transforms a single component from normalized GraphQL data to ComponentProps
 *
 * @param component - Normalized component data with __component field
 * @returns Transformed component or null if invalid
 */
function transformComponent(component: any): ComponentProps | null {
    if (!component || !component.__component) {
        return null;
    }

    const componentType = component.__component as ComponentsType;

    if (componentType === ComponentsType.TABBED_CONTENT) {
        return transformTabbedContentComponent(component);
    }

    // Transform common nested structures
    // Copy all fields first, then transform nested structures
    const transformed: any = {
        ...component,
        id: parseInt(component.id || component.documentId || "0", 10),
    };

    // Transform CTA
    if (component.cta) {
        const cta = transformCta(component.cta);
        if (cta) {
            transformed.cta = cta;
        } else {
            delete transformed.cta;
        }
    }

    // Transform responsive images (skip for components with plain media fields)
    if (component.image && componentType !== ComponentsType.CLINICS_MAP) {
        transformed.image = transformResponsiveImage(component.image);
    }

    // Transform poster image (Video component)
    if (component.poster) {
        transformed.poster = transformResponsiveImage(component.poster);
    }

    // Transform nested arrays with images
    if (component.items && Array.isArray(component.items)) {
        transformed.items = component.items.map((item: any) => {
            const transformedItem: any = { ...item };

            for (const [alias, original] of Object.entries(ALIAS_MAP)) {
                if (transformedItem[alias] !== undefined) {
                    transformedItem[original] = transformedItem[alias];
                    delete transformedItem[alias];
                }
            }

            // Transform image arrays in items (for MultiColumnSlider)
            if (item.image && Array.isArray(item.image)) {
                transformedItem.image = item.image.map((img: any) =>
                    transformResponsiveImage(img)
                );
            }

            // Transform single image in items
            if (item.image && !Array.isArray(item.image)) {
                transformedItem.image = transformResponsiveImage(item.image);
            }

            // Transform captions array
            if (item.captions && Array.isArray(item.captions)) {
                transformedItem.captions = item.captions.map((caption: any) =>
                    transformText(caption)
                );
            }

            // Transform icon
            if (item.icon) {
                transformedItem.icon = transformIcon(item.icon);
            }

            // Remove undefined values from item
            return removeUndefined(transformedItem);
        });
    }

    // Transform advantages array with type safety
    const transformedAdvantages = transformAdvantages(component, componentType);
    if (transformedAdvantages !== undefined) {
        transformed.advantages = transformedAdvantages;
    }

    // Transform slogans array
    if (component.slogans && Array.isArray(component.slogans)) {
        transformed.slogans = component.slogans.map((slogan: any) =>
            transformText(slogan)
        );
    }

    // Transform questions structure
    if (component.questions) {
        transformed.questions = {
            ...component.questions,
            items: component.questions.items?.map((item: any) => {
                const transformedItem: any = { ...item };
                if (item.icon) {
                    transformedItem.icon = transformIcon(item.icon);
                }
                // Remove undefined values from question item
                return removeUndefined(transformedItem);
            }),
        };
    }

    // Transform trustpilotLink (only for Link objects, not plain strings)
    if (
        component.trustpilotLink &&
        typeof component.trustpilotLink === "object"
    ) {
        transformed.trustpilotLink = transformLink(component.trustpilotLink);
    }

    // Remove all undefined values before returning
    return removeUndefined(transformed) as ComponentProps;
}

/**
 * Transforms CTA from GraphQL format to frontend format
 */
function transformCta(cta: any): ElementCtaProps | null {
    if (!cta) {
        return null;
    }

    const transformed: ElementCtaProps = {
        text: cta.text || "",
        ...(cta.variant && { variant: cta.variant }),
    };

    if (cta.link?.data?.attributes) {
        transformed.link = {
            href: cta.link.data.attributes.href || "",
        };
    } else if (cta.link?.href) {
        transformed.link = {
            href: cta.link.href,
        };
    }

    // Transform iconRight
    if (cta.iconRight) {
        transformed.iconRight = transformIcon(cta.iconRight);
    }

    return transformed;
}

/**
 * Transforms responsive image from GraphQL format to frontend format
 */
export function transformResponsiveImage(
    image: any
): ElementResponsiveImageProps {
    if (!image)
        return {
            desktop: { url: "" },
            mobile: { url: "" },
        };

    const desktop =
        image.desktop?.data?.attributes ||
        image.desktop?.attributes ||
        image.desktop ||
        {};
    const mobile =
        image.mobile?.data?.attributes ||
        image.mobile?.attributes ||
        image.mobile ||
        {};

    return {
        desktop: {
            url: getImageUrl(desktop.url || "", strapiBase),
            ...(desktop.width && { width: desktop.width }),
            ...(desktop.height && { height: desktop.height }),
        },
        mobile: {
            url: getImageUrl(mobile.url || "", strapiBase),
            ...(mobile.width && { width: mobile.width }),
            ...(mobile.height && { height: mobile.height }),
        },
    };
}

/**
 * Transforms text element
 */
function transformText(text: any): { text: string } {
    if (typeof text === "string") {
        return { text };
    }
    return { text: text?.text || "" };
}

/**
 * Transforms icon element
 */
function transformIcon(icon: any): { type: string } {
    return {
        type: icon?.type || "",
    };
}

/**
 * Transforms link element
 */
function transformLink(link: any): { href: string } {
    if (link?.data?.attributes) {
        return {
            href: link.data.attributes.href || "",
        };
    }
    return {
        href: link?.href || "",
    };
}

/**
 * Transforms an SEO image (metaImage or ogImage) to frontend shape.
 */
function transformSeoImage(
    image: { url?: string; width?: number; height?: number } | null | undefined
): { url: string; width?: number; height?: number } | null {
    if (!image || !image.url) {
        return null;
    }
    return {
        url: image.url || "",
        ...(image.width != null && { width: image.width }),
        ...(image.height != null && { height: image.height }),
    };
}

/**
 * Transforms SEO block from GraphQL to frontend shape (flat, serializable).
 */
function transformSeo(seo: any): CmsPageSeoProps | null {
    if (!seo || typeof seo !== "object") {
        return null;
    }
    const metaImage = transformSeoImage(seo.metaImage);
    const ogImage = transformSeoImage(seo.openGraph?.ogImage);
    const openGraph = seo.openGraph
        ? {
              ogTitle: seo.openGraph.ogTitle || "",
              ogDescription: seo.openGraph.ogDescription || "",
              ...(seo.openGraph.ogType != null && {
                  ogType: seo.openGraph.ogType,
              }),
              ...(seo.openGraph.ogUrl != null && {
                  ogUrl: seo.openGraph.ogUrl,
              }),
              ...(ogImage && { ogImage }),
          }
        : null;
    const result: CmsPageSeoProps = {
        metaTitle: seo.metaTitle ?? "",
        metaDescription: seo.metaDescription ?? "",
        ...(seo.canonicalURL != null && { canonicalURL: seo.canonicalURL }),
        ...(seo.metaRobots != null && { metaRobots: seo.metaRobots }),
        ...(seo.keywords != null && { keywords: seo.keywords }),
        ...(seo.metaViewport != null && { metaViewport: seo.metaViewport }),
        ...(metaImage && { metaImage }),
        ...(openGraph && { openGraph }),
    };
    return removeUndefined(result) as CmsPageSeoProps;
}

/**
 * Transforms localizations array
 */
function transformLocalizations(
    localizations: any
): CmsPageContentProps["localizations"] {
    // localizations is already an array from GraphQL (not wrapped in data)
    if (!Array.isArray(localizations) || localizations.length === 0) {
        return undefined;
    }

    return localizations.map((loc: any) => ({
        slug: loc.slug || "",
        locale: (loc.locale as I18nLocale) || I18nLocale.SPAIN_ES,
    }));
}

/**
 * Transforms GraphQL response data to CmsBlogPostProps format
 *
 * @param graphqlData - Array of page data from GraphQL query
 * @param locale - Locale string for the post
 * @returns Transformed page data or null if no data
 */
export function transformBlogPostData(
    graphqlData: any,
    locale: StrapiLocale
): BlogPostProps | null {
    if (!Array.isArray(graphqlData) || graphqlData.length === 0) {
        return null;
    }

    const i18nLocale = StrapiLocaleToI18n[locale as StrapiLocale];
    const blogPost = graphqlData[0];

    const transformedSeo = transformSeo(blogPost.seo);
    const transformedLocalizations =
        buildCmsBlogPostHreflangAlternates(
            { localizations: transformLocalizations(blogPost.localizations) },
            i18nLocale,
            blogPost.slug
        ) || [];
    const transformedFAQ: QuestionsProps | undefined = blogPost.faq
        ? {
              content: {
                  ...blogPost.faq,
                  title: blogPost.faq.questionsTitle,
                  items: mapItemsWithIcon(blogPost.faq.items),
              },
          }
        : undefined;

    const createTableOfContents = (content: BlocksContent) => {
        if (!content || !Array.isArray(content)) {
            return [];
        }
        const headings = content
            .filter(({ type, level }: any) => type === "heading" && level === 2)
            .map(({ children }: any) => {
                const fullText = extractTextFromStrapiBlockNode(children);
                return {
                    name: cleanHtml(fullText),
                    link: `#${kebabCase(fullText)}`,
                };
            });

        return headings;
    };

    const addReadingTime = (content: BlocksContent, locale: I18nLocale) => {
        const language = UTSLocaleToLanguage[I18nLocaleToUTSLocale[locale]];
        const minutes = getMinutesOfReading(content ?? []);
        return formatDurationInMinutes(minutes, language);
    };

    const result: BlogPostProps = {
        documentId: blogPost.documentId || "",
        name: blogPost.name || "",
        slug: blogPost.slug || "",
        locale: i18nLocale || I18nLocale.SPAIN_ES,
        ...(transformedSeo && { seo: transformedSeo }),
        ...(transformedLocalizations && {
            localizations: transformedLocalizations,
        }),
        cover: transformResponsiveImage(blogPost.cover),
        content: blogPost.content || "",
        content_preview: blogPost.content_preview || "",
        author: blogPost.author
            ? {
                  name: blogPost.author.name,
                  slug: blogPost.author.slug,
              }
            : null,
        publication_date: blogPost.publication_date || "",
        tags: blogPost.tags || [],
        faq: transformedFAQ,
        seo: transformedSeo,
        tableOfContents: createTableOfContents(blogPost.content),
        readingTime: addReadingTime(blogPost.content, i18nLocale),
    };
    // Remove all undefined values for Next.js JSON serialization
    return removeUndefined(result) as BlogPostProps;
}
